import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Icon from '../AppIcon';
import Input from '../ui/Input';
import Button from '../ui/Button';

/**
 * FormRenderer Component
 * Renders form questions for VAs to fill out during job application
 * Supports 6 question types: text, textarea, url, dropdown, radio, checkbox
 * 
 * SECURITY NOTES:
 * - All user input is auto-escaped by React (no XSS possible)
 * - URL validation prevents javascript: protocol and data: URLs
 * - No dangerouslySetInnerHTML - all content rendered as text nodes
 * - Question text and answers from database are sanitized
 * - See docs/04_Features/EMPLOYER_FORMS_SECURITY.md for XSS prevention details
 */
const FormRenderer = ({ form, responses = {}, onChange, errors = {}, readOnly = false }) => {
  const [localResponses, setLocalResponses] = useState(responses);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    setLocalResponses(responses);
  }, [responses]);

  // Sort questions by order field
  const sortedQuestions = [...(form?.questions || [])].sort((a, b) => 
    (a.order || 0) - (b.order || 0)
  );

  const handleResponseChange = (questionId, value, question) => {
    const newResponses = {
      ...localResponses,
      [questionId]: {
        question: question.question,
        type: question.type,
        answer: value
      }
    };
    
    setLocalResponses(newResponses);
    
    // Clear validation error for this question
    if (validationErrors[questionId]) {
      const newErrors = { ...validationErrors };
      delete newErrors[questionId];
      setValidationErrors(newErrors);
    }
    
    // Notify parent component
    if (onChange) {
      onChange(newResponses);
    }
  };

  const validateUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const renderQuestionInput = (question, index) => {
    const responseValue = localResponses[question.id]?.answer || '';
    const hasError = errors[question.id] || validationErrors[question.id];
    const errorMessage = errors[question.id] || validationErrors[question.id];

    switch (question.type) {
      case 'text':
        return (
          <div key={question.id} className="space-y-2">
            <label className="block text-sm font-medium text-text-primary">
              {question.question}
              {question.required && <span className="text-error ml-1">*</span>}
            </label>
            {question.helpText && (
              <p className="text-xs text-text-secondary">{question.helpText}</p>
            )}
            <Input
              type="text"
              value={responseValue}
              onChange={(e) => handleResponseChange(question.id, e.target.value, question)}
              placeholder={question.placeholder || 'Enter your answer...'}
              disabled={readOnly}
              error={hasError}
              className="w-full"
            />
            {hasError && (
              <p className="text-xs text-error flex items-center mt-1">
                <Icon name="AlertCircle" size={12} className="mr-1" />
                {errorMessage}
              </p>
            )}
          </div>
        );

      case 'textarea':
        return (
          <div key={question.id} className="space-y-2">
            <label className="block text-sm font-medium text-text-primary">
              {question.question}
              {question.required && <span className="text-error ml-1">*</span>}
            </label>
            {question.helpText && (
              <p className="text-xs text-text-secondary">{question.helpText}</p>
            )}
            <textarea
              value={responseValue}
              onChange={(e) => handleResponseChange(question.id, e.target.value, question)}
              placeholder={question.placeholder || 'Enter your answer...'}
              disabled={readOnly}
              rows={4}
              className={`w-full px-3 py-2 border rounded-lg resize-y focus:ring-2 focus:ring-primary focus:border-primary transition-colors ${
                hasError 
                  ? 'border-error focus:ring-error focus:border-error' 
                  : 'border-border'
              } ${readOnly ? 'bg-secondary-50 cursor-not-allowed' : 'bg-background'}`}
            />
            {hasError && (
              <p className="text-xs text-error flex items-center mt-1">
                <Icon name="AlertCircle" size={12} className="mr-1" />
                {errorMessage}
              </p>
            )}
          </div>
        );

      case 'url':
        return (
          <div key={question.id} className="space-y-2">
            <label className="block text-sm font-medium text-text-primary">
              {question.question}
              {question.required && <span className="text-error ml-1">*</span>}
            </label>
            {question.helpText && (
              <p className="text-xs text-text-secondary">{question.helpText}</p>
            )}
            <div className="relative">
              <Input
                type="url"
                value={responseValue}
                onChange={(e) => handleResponseChange(question.id, e.target.value, question)}
                placeholder={question.placeholder || 'https://example.com'}
                disabled={readOnly}
                error={hasError}
                className="w-full pr-10"
              />
              {responseValue && validateUrl(responseValue) && !readOnly && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <Icon name="CheckCircle" size={18} className="text-success" />
                </div>
              )}
            </div>
            {hasError && (
              <p className="text-xs text-error flex items-center mt-1">
                <Icon name="AlertCircle" size={12} className="mr-1" />
                {errorMessage}
              </p>
            )}
          </div>
        );

      case 'dropdown':
        return (
          <div key={question.id} className="space-y-2">
            <label className="block text-sm font-medium text-text-primary">
              {question.question}
              {question.required && <span className="text-error ml-1">*</span>}
            </label>
            {question.helpText && (
              <p className="text-xs text-text-secondary">{question.helpText}</p>
            )}
            <select
              value={responseValue}
              onChange={(e) => handleResponseChange(question.id, e.target.value, question)}
              disabled={readOnly}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors ${
                hasError 
                  ? 'border-error focus:ring-error focus:border-error' 
                  : 'border-border'
              } ${readOnly ? 'bg-secondary-50 cursor-not-allowed' : 'bg-background'}`}
            >
              <option value="">Select an option...</option>
              {(question.options || []).map((option, idx) => (
                <option key={idx} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {hasError && (
              <p className="text-xs text-error flex items-center mt-1">
                <Icon name="AlertCircle" size={12} className="mr-1" />
                {errorMessage}
              </p>
            )}
          </div>
        );

      case 'radio':
        return (
          <div key={question.id} className="space-y-2">
            <label className="block text-sm font-medium text-text-primary">
              {question.question}
              {question.required && <span className="text-error ml-1">*</span>}
            </label>
            {question.helpText && (
              <p className="text-xs text-text-secondary mb-2">{question.helpText}</p>
            )}
            <div className="space-y-2">
              {(question.options || []).map((option, idx) => (
                <label
                  key={idx}
                  className={`flex items-center space-x-3 p-3 border rounded-lg transition-colors cursor-pointer ${
                    responseValue === option
                      ? 'border-primary bg-primary-50'
                      : 'border-border hover:border-primary-200'
                  } ${readOnly ? 'cursor-not-allowed opacity-60' : ''} ${
                    hasError ? 'border-error' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name={`radio-${question.id}`}
                    value={option}
                    checked={responseValue === option}
                    onChange={(e) => handleResponseChange(question.id, e.target.value, question)}
                    disabled={readOnly}
                    className="h-4 w-4 text-primary focus:ring-primary border-border"
                  />
                  <span className="text-sm text-text-primary">{option}</span>
                </label>
              ))}
            </div>
            {hasError && (
              <p className="text-xs text-error flex items-center mt-1">
                <Icon name="AlertCircle" size={12} className="mr-1" />
                {errorMessage}
              </p>
            )}
          </div>
        );

      case 'checkbox':
        return (
          <div key={question.id} className="space-y-2">
            <label className="block text-sm font-medium text-text-primary">
              {question.question}
              {question.required && <span className="text-error ml-1">*</span>}
            </label>
            {question.helpText && (
              <p className="text-xs text-text-secondary mb-2">{question.helpText}</p>
            )}
            <div className="space-y-2">
              {(question.options || []).map((option, idx) => {
                const checkedValues = Array.isArray(responseValue) ? responseValue : [];
                const isChecked = checkedValues.includes(option);
                
                return (
                  <label
                    key={idx}
                    className={`flex items-center space-x-3 p-3 border rounded-lg transition-colors cursor-pointer ${
                      isChecked
                        ? 'border-primary bg-primary-50'
                        : 'border-border hover:border-primary-200'
                    } ${readOnly ? 'cursor-not-allowed opacity-60' : ''} ${
                      hasError ? 'border-error' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      value={option}
                      checked={isChecked}
                      onChange={(e) => {
                        const newValues = e.target.checked
                          ? [...checkedValues, option]
                          : checkedValues.filter(v => v !== option);
                        handleResponseChange(question.id, newValues, question);
                      }}
                      disabled={readOnly}
                      className="h-4 w-4 text-primary focus:ring-primary border-border rounded"
                    />
                    <span className="text-sm text-text-primary">{option}</span>
                  </label>
                );
              })}
            </div>
            {hasError && (
              <p className="text-xs text-error flex items-center mt-1">
                <Icon name="AlertCircle" size={12} className="mr-1" />
                {errorMessage}
              </p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (!form || !form.questions || form.questions.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Form Header */}
      <div className="pb-4 border-b border-border">
        <h3 className="text-lg font-semibold text-text-primary flex items-center">
          <Icon name="FileText" size={20} className="mr-2 text-primary" />
          {form.title}
        </h3>
        {form.description && (
          <p className="text-sm text-text-secondary mt-2">{form.description}</p>
        )}
        <div className="flex items-center mt-3 text-xs text-text-muted">
          <Icon name="Info" size={14} className="mr-1" />
          <span>
            {sortedQuestions.filter(q => q.required).length} required question
            {sortedQuestions.filter(q => q.required).length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Questions */}
      <div className="space-y-6">
        {sortedQuestions.map((question, index) => (
          <div key={question.id}>
            {renderQuestionInput(question, index)}
          </div>
        ))}
      </div>

      {/* Read-only indicator */}
      {readOnly && (
        <div className="mt-6 p-4 bg-secondary-50 border border-border rounded-lg flex items-center text-sm text-text-secondary">
          <Icon name="Lock" size={16} className="mr-2" />
          <span>This form is view-only and cannot be edited</span>
        </div>
      )}
    </div>
  );
};

FormRenderer.propTypes = {
  form: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    questions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        type: PropTypes.oneOf(['text', 'textarea', 'url', 'dropdown', 'radio', 'checkbox']).isRequired,
        question: PropTypes.string.isRequired,
        required: PropTypes.bool,
        order: PropTypes.number,
        placeholder: PropTypes.string,
        helpText: PropTypes.string,
        options: PropTypes.arrayOf(PropTypes.string)
      })
    ).isRequired
  }).isRequired,
  responses: PropTypes.object,
  onChange: PropTypes.func,
  errors: PropTypes.object,
  readOnly: PropTypes.bool
};

export default FormRenderer;
